import { EventDeliveryWidget } from "@components/event-delivery";
import { PhoneNumbersWidget } from "@components/virtual-receptionist/widget";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSwapy } from "swapy";
import { useModals } from "../../context/ModalContext";
import { selectCurrentCompany, selectRole } from "../../redux/features/user/selectors/userState.selectors";
import { updateUser } from "../../redux/features/userStateSlice";
import { IRoles } from "../../utils";
import { AdminStaffWidget } from "../admins/AdminStaffWidget";
import { EditableCompanyAvatar } from "../common/CompanyAvatar";
import { WidgetComponentMaps } from "../common/WidgetComponentMaps";
import { HoursOfOperationWidget } from "../hoursOfOperation/Widget";
import { InvoiceNumberingWidget } from "../invoice-numbering/InvoiceNumberingWidget";
import { MapsAndCoveragesWidget } from "../mapsAndCoverages/Widget";
import { ServicesReminder } from "../organization-reminder/components/principals/ServiceReminder";
import { VaultWidget } from "../password-baul/components/VaultWidget";
import { PayoutMethodsWidget } from "../payments/PayoutMethods";
import { ServicesWidget } from "../services/Widget";
import { TimeSheetWidget } from "../time-sheet/component/TimeSheetWidget";
import { colors } from "../ui/colors";

export const OrganizationLayout = () => {
  const swapy = useRef(null);
  const container = useRef(null);
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const currentCompany = useSelector(selectCurrentCompany);
  const role = useSelector(selectRole);
  const isSuperAdmin = role === IRoles.SuperAdmin;

  useEffect(() => {
    if (isMobile) return;

    if (container.current) {
      swapy.current = createSwapy(container.current);
      swapy.current.onSwap((event) => {
        console.log("swap", event);
      });
    }

    return () => {
      swapy.current?.destroy();
    };
  }, [isMobile]);

  const { modals } = useModals();
  const organizationModals = modals.filter((m) => m.key.startsWith("Organization")).map((m) => m.key);

  if (isSuperAdmin) {
    return (
      <Box>
        <Grid
          ref={container}
          container
          spacing={isMobile ? 0 : 1}
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: colors.warmWhite.base,
          }}
        >
          <Grid item xs={12} sx={{ mb: isMobile ? 1 : 0 }}>
            <WidgetComponentMaps
              swapyName="organizationHeader"
              title={currentCompany?.businessName || "Organization"}
              description="Manage your organization details and settings."
              icon={<EditableCompanyAvatar company={currentCompany} size={48} />}
              customIcon
              sxProp={{
                minHeight: "auto",
                padding: "16px 24px",
              }}
            />
          </Grid>

          {/* SuperAdmin balanced layout */}
          <Grid item xs={12} lg={6}>
            <AdminStaffWidget swapyName="adminStaffWidge" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <PhoneNumbersWidget swapyName="phoneNumberWidget" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <VaultWidget />
          </Grid>
          <Grid item xs={12} lg={6}>
            <EventDeliveryWidget swapyName="eventDeliveryWidget" />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Grid
        ref={container}
        container
        spacing={isMobile ? 0 : 1}
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: colors.warmWhite.base,
        }}
      >
        <Grid item xs={12} sx={{ mb: isMobile ? 1 : 0 }}>
          <WidgetComponentMaps
            swapyName="organizationHeader"
            config={true}
            title={currentCompany?.businessName || "Organization"}
            description="Manage your organization details and settings."
            icon={<EditableCompanyAvatar company={currentCompany} size={48} />}
            customIcon
            sxProp={{
              minHeight: "auto",
              padding: "16px 24px",
            }}
          />
        </Grid>

        {/* Left Column */}
        <Grid item xs={12} lg={6}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <MapsAndCoveragesWidget updateUser={updateUser} swapyName="mapsAndCoveragesWidget" />
            </Grid>
            <Grid item xs={12}>
              <HoursOfOperationWidget swapyName="hoursOfOperationWidget" />
            </Grid>
            <Grid item xs={12}>
              <ServicesWidget swapyName="servicesWidget" />
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} lg={6}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <AdminStaffWidget swapyName="adminStaffWidge" />
            </Grid>
            <Grid item xs={12}>
              <PhoneNumbersWidget swapyName="phoneNumberWidget" />
            </Grid>
            {role !== IRoles.Admin && role !== IRoles.SuperAdmin && (
              <Grid item xs={12}>
                <ServicesReminder swapyName="servicesReminder" />
              </Grid>
            )}
            <Grid item xs={12}>
              <TimeSheetWidget />
            </Grid>
            <Grid item xs={12}>
              <VaultWidget />
            </Grid>
            <Grid item xs={12}>
              <PayoutMethodsWidget swapyName="payoutMethodsWidget" />
            </Grid>
            <Grid item xs={12}>
              <InvoiceNumberingWidget swapyName="invoiceNumberingWidget" />
            </Grid>
            <Grid item xs={12}>
              <EventDeliveryWidget swapyName="eventDeliveryWidget" />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* {organizationModals.length > 0 && location.pathname === "/business/organization" && <ToolKitDashboard mode="organization" />} */}
    </Box>
  );
};
